import type {
  CustomerAddress,
  GeoCoordinates,
  GeocodingSource,
  Location,
  PhoneNumber,
  PostalAddress,
  Site,
} from '@klareospace/pharos-lib-types';
import { GEOCODING_SOURCE } from '@klareospace/pharos-lib-types';
import type { LegacyDeliveryAddress } from '@klareospace/pharos-lib-utils';
import { ratioToPercent } from '@klareospace/pharos-lib-utils';

/** Latitude/longitude pair collected from a site/address form, before geocoding resolves a score. */
export interface SiteCoordinatesFormInput {
  latitude?: number;
  longitude?: number;
}

/** Initial address data used to seed the AddressInput component with existing values. */
export interface AddressInputInitialAddress {
  city?: string;
  coordinates?: SiteCoordinatesFormInput;
  country?: string;
  geocodingScore?: number;
  geocodingSource?: GeocodingSource;
  houseNumber?: string;
  postalCode?: string;
  street?: string;
}

/** Shared address form field shape used by every feature composing AddressInput (sites, customers, tenants...). */
export interface AddressFormInput {
  additionalInformations?: string;
  city: string;
  complement?: string;
  country: string;
  digicode?: string;
  floor?: string;
  hasElevator?: boolean;
  houseNumber?: string;
  postalCode: string;
  street?: string;
}

export interface BuildSiteLocationOptions {
  address: AddressFormInput;
  coordinates: SiteCoordinatesFormInput;
  source: GeocodingSource;
}

/**
 * Builds a `Location` payload from address form values and coordinate form values.
 * Score is always 1 (convention: front-end geocodes are considered trusted).
 * Source is derived from the geocodingSource form field. Returns `undefined` when coordinates
 * are not yet resolved (the caller decides whether that is blocking).
 */
export function buildSiteLocation({ address, coordinates, source }: BuildSiteLocationOptions): Location | undefined {
  if (coordinates.latitude === undefined || coordinates.longitude === undefined) return undefined;
  const geoCoords: GeoCoordinates = {
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    score: 1,
    source,
  };
  return {
    address: toPostalAddress(address),
    coordinates: geoCoords,
  };
}

/** Discriminated union of address complement part types. */
export type AddressComplementPart =
  | { type: 'additionalInfo'; value: string }
  | { type: 'complement'; value: string }
  | { type: 'digicode'; value: string }
  | { type: 'elevator'; hasElevator: boolean }
  | { type: 'floor'; value: string };

/**
 * Converts address form values to a PostalAddress payload.
 * Optional fields are only included when non-empty to avoid sending blank strings to the API.
 *
 * Accepts any value structurally compatible with PostalAddress (including form value types
 * where city, country and postalCode are required strings).
 */
export function toPostalAddress(address: PostalAddress): PostalAddress {
  return {
    city: address.city,
    country: address.country,
    ...collectOptionalAddressFields(address),
  };
}

/**
 * Extract address complement fields as structured parts.
 * Returns an empty array when no complement information is present.
 * Callers render the parts using their own i18n function.
 *
 * Accepts `Partial<PostalAddress>` so callers can pass a PostalAddress
 * (e.g. from `Location.address` or `SiteSummary.address`) without a cast.
 */
export function extractAddressComplementParts(address: Partial<PostalAddress> | undefined): AddressComplementPart[] {
  if (!address) return [];
  const parts: AddressComplementPart[] = [];
  if (address.complement) parts.push({ type: 'complement', value: address.complement });
  if (address.floor) parts.push({ type: 'floor', value: address.floor });
  if (address.hasElevator !== undefined) parts.push({ type: 'elevator', hasElevator: address.hasElevator });
  if (address.digicode) parts.push({ type: 'digicode', value: address.digicode });
  if (address.additionalInformations) parts.push({ type: 'additionalInfo', value: address.additionalInformations });
  return parts;
}

/**
 * Builds the initialAddress prop for AddressInput from a resolved `Location`.
 * Returns undefined for undefined input so AddressInput renders in its empty state.
 */
export function buildInitialAddressFromLocation(
  location: Location | undefined,
): AddressInputInitialAddress | undefined {
  if (!location) {
    return undefined;
  }
  return toAddressInputInitialAddress(location.address, location.coordinates);
}

// ─── Legacy-tolerant customer address resolution ──────────────────────────────

/**
 * Legacy persisted customer address shape: a flat `Partial<PostalAddress>` with an
 * optional `title`, legacy `{x, y}` coordinates, and no `location` wrapper. Unmigrated
 * customer documents still reach the frontend in this shape.
 */
export interface LegacyCustomerAddress extends LegacyDeliveryAddress {
  coordinates?: { x: number; y: number };
  geocodingScore?: number;
  geocodingSource?: GeocodingSource;
  isDefault?: boolean;
  phone?: PhoneNumber;
}

export interface ResolvedCustomerAddressFields {
  address: Partial<PostalAddress> | undefined;
  coordinates: GeoCoordinates | undefined;
  isDefault: boolean | undefined;
}

/**
 * Resolves postal fields, coordinates, and the `isDefault` flag from a customer's stored
 * address, tolerant of both the current `CustomerAddress` shape (`location.address` +
 * `location.coordinates`) and the legacy flat shape (flat fields + `{x, y}`
 * coordinates, no `location` wrapper). Mirrors migration 0001's `transformCoords`
 * (x = longitude, y = latitude) so legacy and migrated documents resolve identically.
 */
export function resolveCustomerAddressFields(
  address: CustomerAddress | LegacyCustomerAddress | undefined,
): ResolvedCustomerAddressFields {
  if (!address) {
    return { address: undefined, coordinates: undefined, isDefault: undefined };
  }

  if ('location' in address) {
    return {
      address: address.location?.address,
      coordinates: address.location?.coordinates,
      isDefault: address.location?.isDefault,
    };
  }

  return {
    address,
    coordinates: resolveLegacyCustomerCoordinates(address),
    isDefault: address.isDefault,
  };
}

/**
 * Builds the initialAddress prop for AddressInput from a customer's stored first address,
 * tolerant of both the current `CustomerAddress` shape and the legacy flat shape.
 * Returns undefined for undefined input so AddressInput renders in its empty state.
 */
export function buildInitialAddressFromCustomerAddress(
  address: CustomerAddress | LegacyCustomerAddress | undefined,
): AddressInputInitialAddress | undefined {
  if (!address) return undefined;
  const { address: postalAddress, coordinates } = resolveCustomerAddressFields(address);
  return toAddressInputInitialAddress(postalAddress, coordinates);
}

// ─── Legacy-tolerant site address resolution ──────────────────────────────────

/**
 * Legacy persisted site shape: flat `address`/`coordinates` fields directly on the site
 * document (no `location` wrapper). Unmigrated site documents still reach the frontend in this
 * shape while `location` remains undefined.
 */
export interface LegacySiteFields {
  address?: Partial<PostalAddress>;
  coordinates?: { x: number; y: number };
  geocodingScore?: number;
  geocodingSource?: GeocodingSource;
}

export type SiteWithLegacyFields = Site & LegacySiteFields;

export interface ResolvedSiteAddressFields {
  address: Partial<PostalAddress> | undefined;
  coordinates: GeoCoordinates | undefined;
}

/**
 * Resolves postal fields + coordinates from a site, tolerant of both the current
 * `location`-wrapped shape and the legacy flat shape (flat `address` + top-level
 * `{x, y}` `coordinates`, no `location` wrapper). Mirrors migration 0001's
 * `transformSiteToLocation` (x = longitude, y = latitude, score defaults to 1) so legacy and
 * migrated documents resolve identically.
 */
export function resolveSiteAddressFields(site: SiteWithLegacyFields | undefined): ResolvedSiteAddressFields {
  if (!site) return { address: undefined, coordinates: undefined };

  if (site.location) {
    return { address: site.location.address, coordinates: site.location.coordinates };
  }

  const legacyCoords = site.coordinates;
  const coordinates: GeoCoordinates | undefined = legacyCoords
    ? {
        latitude: legacyCoords.y,
        longitude: legacyCoords.x,
        score: site.geocodingScore ?? 1,
        source: site.geocodingSource ?? GEOCODING_SOURCE.MANUAL,
      }
    : undefined;

  return { address: site.address, coordinates };
}

/**
 * Builds the initialAddress prop for AddressInput from a site, tolerant of both the current
 * `location`-wrapped shape and the legacy flat shape. Returns undefined for
 * undefined input so AddressInput renders in its empty state.
 */
export function buildInitialAddressFromSite(
  site: SiteWithLegacyFields | undefined,
): AddressInputInitialAddress | undefined {
  if (!site) return undefined;
  const { address, coordinates } = resolveSiteAddressFields(site);
  return toAddressInputInitialAddress(address, coordinates);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Optional string fields of PostalAddress that must be trimmed before inclusion.
 * Listed alphabetically; kept as a module-level constant so collectOptionalAddressFields
 * can iterate rather than branch per-field (stays under the complexity limit).
 */
const OPTIONAL_STRING_ADDRESS_FIELDS = [
  'additionalInformations',
  'complement',
  'digicode',
  'floor',
  'houseNumber',
  'postalCode',
  'street',
] as const;

/**
 * Collects optional PostalAddress fields, trimming strings and omitting blank/undefined values.
 * Extracted to keep toPostalAddress under the cyclomatic complexity limit.
 */
function collectOptionalAddressFields(address: PostalAddress): Partial<PostalAddress> {
  const fields: Partial<PostalAddress> = {};
  for (const key of OPTIONAL_STRING_ADDRESS_FIELDS) {
    const value = address[key]?.trim();
    if (value) fields[key] = value;
  }
  if (address.hasElevator !== undefined) fields.hasElevator = address.hasElevator;
  return fields;
}

/**
 * Converts legacy `{x, y}` customer address coordinates to `GeoCoordinates`.
 * Mirrors migration 0001's `transformCoords` (x = longitude, y = latitude) and
 * `computeScore` (defaults to 0 when no score is stored).
 */
function resolveLegacyCustomerCoordinates(address: LegacyCustomerAddress): GeoCoordinates | undefined {
  const legacyCoords = address.coordinates;
  if (!legacyCoords) return undefined;
  return {
    latitude: legacyCoords.y,
    longitude: legacyCoords.x,
    score: address.geocodingScore ?? 0,
    source: address.geocodingSource ?? GEOCODING_SOURCE.MANUAL,
  };
}

/**
 * Converts a geocoding score ratio (0–1) to the percentage (0–100) AddressInput expects.
 * Returns undefined when no score is available. Extracted to keep
 * toAddressInputInitialAddress under the cyclomatic complexity limit.
 */
function toDisplayGeocodingScore(coordinates: GeoCoordinates | undefined): number | undefined {
  if (coordinates?.score === undefined) return undefined;
  return ratioToPercent(coordinates.score, 0);
}

/**
 * Converts resolved postal fields + coordinates into the AddressInput initial-value shape,
 * converting the geocoding score from ratio (0–1) to percentage (0–100).
 */
function toAddressInputInitialAddress(
  address: Partial<PostalAddress> | undefined,
  coordinates: GeoCoordinates | undefined,
): AddressInputInitialAddress {
  return {
    city: address?.city,
    coordinates: {
      latitude: coordinates?.latitude,
      longitude: coordinates?.longitude,
    },
    country: address?.country,
    geocodingScore: toDisplayGeocodingScore(coordinates),
    geocodingSource: coordinates?.source,
    houseNumber: address?.houseNumber,
    postalCode: address?.postalCode,
    street: address?.street,
  };
}
